"use client";
import { TechQueryResult } from "@/sanity.types";
import { PortableToBlue } from "../../../utility/richTextComponents/richTextComponents";
import { PortableText } from "next-sanity";
import coreIllustration from "@assets/images/tech_feat_illu/core.svg";
import circlesIllustration from "@assets/images/tech_feat_illu/circles.svg";
import firstIllustration from "@assets/images/tech_feat_illu/first.svg";
import secondIllustration from "@assets/images/tech_feat_illu/second.svg";
import bgIllustration from "@assets/images/tech_feat_illu/bg.svg";
import { AnimatePresence, motion, useMotionValueEvent } from "framer-motion";

import Image from "next/image";
import { useScroll } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import MobileTile from "./MobileTile/MobileTile";

const Features = ({ moduleData }: { moduleData: TechQueryResult["features"] }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const targetRef = useRef<HTMLDivElement>(null);
	const [screenHeight, setScreenHeight] = useState<number>(0);
	const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start center", "end center"] });
	const [scrollY, setScrollY] = useState<number>(0);
	useMotionValueEvent(scrollYProgress, "change", (current) => {
		setScrollY(current);
	});

	useEffect(() => {
		setScreenHeight(window.innerHeight);
	}, []);

	return (
		<>
			{/*  Desktop version */}
			<section className="mx-grid my-44 grid grid-cols-14 sml:mx-grid-s sm:hidden">
				<div className="col-span-6 col-start-2 sml:col-start-1">
					<div
						ref={targetRef}
						className="sticky grid aspect-[394/236] w-full grid-cols-1 grid-rows-1"
						style={{ top: `${screenHeight / 2 - (targetRef.current?.clientHeight || 0) / 2}px` }}
					>
						<Image src={bgIllustration} fill alt="Background" className="col-start-1 row-start-1 object-cover" />
						<Image src={circlesIllustration} fill alt="Circles" className="pulse-low origin-[80%] object-contain" />
						<AnimatePresence>
							{scrollY <= 0.5 ? (
								<motion.div
									initial={{ opacity: 0, x: "2rem" }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: "2rem" }}
									className="col-start-1 row-start-1"
									key="first"
								>
									<Image src={firstIllustration} fill alt="First" className="object-cover" />
								</motion.div>
							) : (
								<motion.div
									initial={{ opacity: 0, x: "2rem" }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: "2rem" }}
									className="col-start-1 row-start-1"
									key="second"
								>
									<Image src={secondIllustration} fill alt="Second" className="object-cover" />
								</motion.div>
							)}
						</AnimatePresence>
						<Image src={coreIllustration} fill alt="Core" className="object-contain" />
					</div>
				</div>
				<div ref={containerRef} className="col-span-5 col-start-9 sml:col-span-7 sml:col-start-8">
					<h2 className="text-h3">
						<PortableToBlue value={moduleData?.title} />
					</h2>
					<div className="flex flex-col gap-y-40">
						{moduleData?.features?.map((feature, index) => (
							<div
								key={index}
								className="mt-12 whitespace-pre-wrap font-secondary text-[#717171] [&>p]:pb-6 [&_li]:ml-6 [&_li]:list-disc [&_li]:pl-2 [&_strong]:font-secondary-semibold [&_strong]:font-medium [&_strong]:text-black"
							>
								<PortableText value={feature.featureDescription || []} />
							</div>
						))}
					</div>
				</div>
			</section>

			{/*  Mobile version */}
			<section className="mx-grid-s my-16 hidden flex-col sm:flex xs:mx-grid-xs">
				<h2 className="text-center text-h2 xs:text-h2-xs">
					<PortableToBlue value={moduleData?.title} />
				</h2>

				<div className="col-span-5 col-start-9 mt-12 sml:col-span-7 sml:col-start-8">
					<div className="flex flex-col gap-y-12">
						{moduleData?.features?.map((feature, index) => <MobileTile key={index} feature={feature} index={index} />)}
					</div>
				</div>
			</section>
		</>
	);
};

export default Features;
